/**
 * Zoom of the document preview (cdb_webos#1477).
 *
 * A zoom is a share of the document's real size, as Word, Excel and
 * PowerPoint count it: 100 % shows a page as large as it is printed. PDF.js
 * draws one PDF point per CSS pixel at scale 1, and a point is 4/3 of a pixel,
 * so the scale handed to it is the zoom times 4/3.
 */

export type FitMode = 'width' | 'page' | 'custom'

export const PT_TO_PX = 4 / 3

/** Steps of the zoom menu and of the − / + buttons. */
export const ZOOM_PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const

// Pinch and Ctrl + wheel go a little past the presets. Fitting goes as far
// down as it takes, so a wide sheet really fits a narrow panel, and up no
// further than the wheel, so a sheet of three cells is not blown up sevenfold.
const WHEEL_RANGE = [0.25, 4] as const
const FIT_RANGE = [0.05, 4] as const

// What the scroll container's padding takes off each side (`p-4`).
export const CONTAINER_PADDING = 16

const EPSILON = 0.005

export interface Size {
  width: number
  height: number
}

/**
 * The zoom at which a page of `page` points fits a container of `box` pixels:
 * its width for 'width', the whole page for 'page'. Rounded down, so the fitted
 * page never sits a fraction of a pixel too wide and brings up a scrollbar.
 */
export function fitZoom(mode: 'width' | 'page', box: Size, page: Size): number | null {
  if (page.width <= 0 || box.width <= 0) return null
  const width = (box.width - 2 * CONTAINER_PADDING) / (page.width * PT_TO_PX)
  const height =
    page.height > 0 && box.height > 0
      ? (box.height - 2 * CONTAINER_PADDING) / (page.height * PT_TO_PX)
      : width
  const zoom = mode === 'width' ? width : Math.min(width, height)
  return clamp(Math.floor(zoom * 1000) / 1000, FIT_RANGE)
}

/** The next preset above `zoom`, or null at 200 %. */
export function zoomInStep(zoom: number): number | null {
  return ZOOM_PRESETS.find((preset) => preset > zoom + EPSILON) ?? null
}

/** The next preset below `zoom`, or null at 50 %. */
export function zoomOutStep(zoom: number): number | null {
  return [...ZOOM_PRESETS].reverse().find((preset) => preset < zoom - EPSILON) ?? null
}

/**
 * The zoom after one wheel event with Ctrl/⌘ held. A trackpad pinch sends many
 * small deltas and a mouse wheel few large ones; capping each event keeps one
 * wheel notch to about a quarter.
 */
export function wheelZoom(zoom: number, deltaY: number): number {
  const delta = Math.max(-25, Math.min(25, deltaY))
  return clamp(Math.round(zoom * Math.exp(-delta / 100) * 1000) / 1000, WHEEL_RANGE)
}

export function isSameZoom(a: number, b: number): boolean {
  return Math.abs(a - b) < EPSILON
}

function clamp(value: number, [min, max]: readonly [number, number]): number {
  return Math.max(min, Math.min(max, value))
}
